import React, { useEffect, useRef, useState } from 'react';
import SkeletonImage from '../Skeleton/skeleton-image-component';
import useScreenSize from '../../Hooks/useScreenSize'; 

const VideoComponent = ({ 
    video, 
    autoPlayWidth = 0, 
    children,
    className = '',
    wrapperClassName = '',
    useSekeleton = true,
}) => {
    const videoRef = useRef(null);
    const [loaded, setLoaded] = useState(false);
    const { width } = useScreenSize(); 

    // Handle Video Load (Fade-in trigger)
    const handleVideoLoad = () => {
        setLoaded(true);
    };

    // 1. Force Video Reload when source changes
    useEffect(() => {
        const videoEl = videoRef.current;
        if (videoEl && video) {
            setLoaded(false); // Reset loader for the new video
            videoEl.load();   // Force browser to fetch the new source
        }
    }, [video]);

    // 2. Robust Autoplay Logic
    useEffect(() => {
        if (!width) return;
        const shouldPlay = width > autoPlayWidth;
        const videoEl = videoRef.current;

        if (videoEl && shouldPlay) {
            const playPromise = videoEl.play();
            if (playPromise !== undefined) {
                playPromise.catch(error => console.warn('Autoplay prevented:', error));
            }
        }
    }, [width, autoPlayWidth, video, loaded]); // Added video and loaded as dependencies

    if (width && width <= autoPlayWidth) return null;

    return (
        <div className={`relative overflow-hidden w-full h-full ${wrapperClassName}`.trim()}>
            
            {/* Skeleton Layer */}
            <div className={`absolute inset-0 z-10 transition-opacity duration-500 ease-in-out ${!useSekeleton || loaded ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
                <SkeletonImage />
            </div>

            {/* Video Layer */}
            <video
                key={video} // IMPORTANT: Forces re-mount when URL changes
                ref={videoRef}
                autoPlay
                loop
                muted
                playsInline
                onLoadedData={handleVideoLoad}
                className={`w-full h-full object-cover transition-opacity duration-700 ease-in-out ${loaded ? 'opacity-100' : 'opacity-0'} ${className}`.trim()}
            >
                <source src={video} type="video/mp4" />
                Your browser does not support the video tag.
            </video>

            {/* Overlay Children */}
            {children && (
                <div className={`absolute top-0 left-0 w-full h-full transition-opacity duration-700 z-20 ${loaded ? 'opacity-100' : 'opacity-0'}`}>
                     <div className="absolute top-0 left-0 w-full h-full dark:bg-gray-900 bg-white opacity-0 hover:opacity-60 rounded-lg transition-all ease-in-out duration-300 flex justify-center items-center">
                        <div className="font-bold text-xl text-black">
                            {children}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VideoComponent;